import { Checkbox, FormControlLabel, Grid } from "@mui/material";
import CustomTextArea from "@/components/CustomTextArea";
import Flex from "@/components/Flex";
import OutlineCardV2 from "@/components/OutlineCardV2";
import Text from "@/components/Text";
import {
  CHART_COLORS,
  CHART_TYPOGRAPHY,
} from "@/components/app/provider/patient/chart";
import {
  CHECKBOX_COLUMN_SIZE,
  CHECKBOX_NOTE_ROWS,
  SESSION_NOTE_LABEL,
} from "../group-visit-constant";
import { SessionCheckboxSection } from "../group-visit-types";
import { parseSelectedNames, toggleSelectedName } from "../group-visit-utils";

export type CheckboxOptionSectionsProps = {
  sections: SessionCheckboxSection[];
  /** Selection strings and notes, keyed by the sections' `key` / `noteKey`. */
  values: Record<string, string>;
  onChange: (field: string, value: string) => void;
  disabled?: boolean;
};

/**
 * Checkbox grid with a free-text note per section, as the group note and the
 * visit-note modals present their option lists.
 *
 * @author Rajkumar Rathod
 */
const CheckboxOptionSections = ({
  sections,
  values,
  onChange,
  disabled,
}: CheckboxOptionSectionsProps) => (
  <Flex flexDirection="column" gap={2} width="100%">
    {sections.map((section) => {
      const selected = parseSelectedNames(values[section.key]);

      return (
        <OutlineCardV2 key={section.key} sx={{ p: 2 }}>
          <Flex flexDirection="column" gap={1} width="100%">
            <Text
              {...CHART_TYPOGRAPHY.body4}
              fontWeight={CHART_TYPOGRAPHY.weight.medium}
              c={CHART_COLORS.textDarker}
            >
              {section.label}
            </Text>

            <Grid container spacing={0.2}>
              {section.items.map((item) => (
                <Grid
                  key={item.name}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: CHECKBOX_COLUMN_SIZE,
                  }}
                >
                  <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                      <Checkbox
                        sx={{ py: 0.25 }}
                        name={`${section.key}.${item.name}`}
                        checked={selected.includes(item.name)}
                        disabled={disabled}
                        onChange={(_, checked) =>
                          onChange(
                            section.key,
                            toggleSelectedName(
                              section.items,
                              selected,
                              item.name,
                              checked
                            )
                          )
                        }
                      />
                    }
                    label={
                      <Text {...CHART_TYPOGRAPHY.body5}>{item.label}</Text>
                    }
                  />
                </Grid>
              ))}

              <Grid
                sx={{
                  mt: 1,
                }}
                size={12}
              >
                <CustomTextArea
                  label={SESSION_NOTE_LABEL.addNote}
                  name={section.noteKey}
                  placeholder={`Notes for ${section.label}...`}
                  value={values[section.noteKey] ?? ""}
                  disabled={disabled}
                  minRows={CHECKBOX_NOTE_ROWS}
                  onChange={(event) =>
                    onChange(section.noteKey, event.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Flex>
        </OutlineCardV2>
      );
    })}
  </Flex>
);

export default CheckboxOptionSections;
